import { useCallback, useEffect, useState } from 'react'

/**
 * Página atual de uma tabela paginada no servidor. Volta para a 1ª página
 * quando `resetKey` (bloco, busca, empresas, ordenação…) muda — derivado no
 * render, sem efeito: com `useEffect` a query disparava primeiro a página
 * antiga do recorte novo e só depois a 1ª.
 */
export function useResettablePage(resetKey: string) {
  const [state, setState] = useState({ key: resetKey, page: 1 })
  const page = state.key === resetKey ? state.page : 1
  const setPage = useCallback(
    (next: number) => setState({ key: resetKey, page: next }),
    [resetKey],
  )
  return [page, setPage] as const
}

/**
 * Se a página atual voltou vazia mas o recorte ainda tem itens (ex.: a
 * última linha da última página foi finalizada), recua para a última página
 * que existe — senão a tela ficava em "nada encontrado", sem rodapé para
 * voltar.
 */
export function useClampToLastPage(
  page: number,
  setPage: (page: number) => void,
  result: { total: number; itemCount: number } | undefined,
  pageSize: number,
) {
  const total = result?.total
  const itemCount = result?.itemCount
  useEffect(() => {
    if (total === undefined || itemCount !== 0 || page <= 1) return
    const last = Math.max(1, Math.ceil(total / pageSize))
    if (page > last) setPage(last)
  }, [total, itemCount, page, pageSize, setPage])
}
